import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { pavoApiService, PavoPaymentRequest } from '../../../../services/pavoApiService';
import { pavoSignalRService } from '../../../../services/pavoSignalRService';
import { useTranslation } from 'react-i18next';

interface PavoOrderTrackingModalProps {
  open: boolean;
  onClose: () => void;
  payments: PavoPaymentRequest[];
  loading?: boolean;
  onPaymentCancelled?: () => void;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);

const formatElapsed = (createdAt: string | undefined, t: (key: string, opts?: any) => string) => {
  if (!createdAt) return '';
  const diff = Date.now() - new Date(createdAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('pavoOrderTracking.justNow');
  if (mins < 60) return t('pavoOrderTracking.minutesAgo', { mins });
  return t('pavoOrderTracking.hoursMinutesAgo', { hours: Math.floor(mins / 60), mins: mins % 60 });
};

const statusLabel = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'pending': return t('pavoOrderTracking.status.pending');
    case 'processing': return t('pavoOrderTracking.status.processing');
    case 'completed': return t('pavoOrderTracking.status.completed');
    case 'failed': return t('pavoOrderTracking.status.failed');
    case 'cancelled': return t('pavoOrderTracking.status.cancelled');
    default: return status;
  }
};

const statusColor = (status: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'pending': return 'info';
    case 'processing': return 'warning';
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const parseRef = (ref: string) => {
  const parts = ref.includes('|') ? ref.split('|') : [ref];
  const tableName = parts[0]?.trim() || '-';
  let shortCode: string | null = null;
  let senderInfo: string | null = null;
  for (let i = 1; i < parts.length; i++) {
    const p = parts[i]?.trim() || '';
    if (p.startsWith('Garson:') || p.startsWith('Kasiyer:')) {
      senderInfo = p;
    } else if (!shortCode && p) {
      shortCode = p;
    }
  }
  return { tableName, shortCode, senderInfo };
};

const PavoOrderTrackingModal: React.FC<PavoOrderTrackingModalProps> = ({
  open,
  onClose,
  payments,
  loading = false,
  onPaymentCancelled,
}) => {
  const { t } = useTranslation('salesSystem');
  const ts = (key: string, opts?: any): string => String(t(key, opts));
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [confirmPayment, setConfirmPayment] = useState<PavoPaymentRequest | null>(null);

  const grouped = useMemo(() => {
    const map: Record<string, PavoPaymentRequest[]> = {};
    payments.forEach(p => {
      const key = p.deviceCode;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [payments]);

  const handleCancelPayment = async (payment: PavoPaymentRequest) => {
    setCancellingId(payment.requestId);
    setConfirmPayment(null);
    try {
      await pavoApiService.cancelPayment(payment.requestId);
      try {
        await pavoSignalRService.cancelPaymentRequest(payment.deviceCode, payment.requestId);
      } catch (_) {}
      onPaymentCancelled?.();
    } catch (err: any) {
      console.error('[PavoTracking] İptal hatası:', err);
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('pavoOrderTracking.title')}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : payments.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            {t('pavoOrderTracking.empty')}
          </Typography>
        ) : (
          Object.entries(grouped).map(([deviceCode, devicePayments]) => (
            <Box key={deviceCode} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PhoneAndroidIcon sx={{ color: '#7c4dff', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  {t('pavoOrderTracking.device')}: {deviceCode.substring(0, 8)}...
                </Typography>
                <Chip label={t('pavoOrderTracking.orderCount', { count: devicePayments.length })} size="small" variant="outlined" />
              </Box>
              {devicePayments.map(payment => {
                const { tableName, shortCode, senderInfo } = parseRef(payment.sourceReference || '');
                const isCancelling = cancellingId === payment.requestId;

                return (
                  <Box
                    key={payment.requestId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      mb: 0.5,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: payment.status === 'pending' ? '#2196f3' : payment.status === 'processing' ? '#ff9800' : '#4caf50',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {tableName}
                      </Typography>
                      {shortCode && (
                        <Typography variant="caption" color="text.secondary">
                          {shortCode}
                        </Typography>
                      )}
                      {senderInfo && (
                        <Typography variant="caption" sx={{ color: '#7c4dff', fontWeight: 600, display: 'block', fontSize: '0.65rem' }}>
                          {senderInfo.replace(':', ': ')}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(payment.totalAmount)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Chip label={statusLabel(payment.status, ts)} color={statusColor(payment.status)} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatElapsed(payment.createdAt, ts)}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setConfirmPayment(payment)}
                        disabled={isCancelling}
                        sx={{ minWidth: 60, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                      >
                        {isCancelling ? <CircularProgress size={16} /> : t('pavoOrderTracking.cancel')}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('pavoOrderTracking.close')}</Button>
      </DialogActions>
    </Dialog>

    {/* İptal Onay Diyalogu */}
    <Dialog open={!!confirmPayment} onClose={() => setConfirmPayment(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{t('pavoOrderTracking.cancelDialog.title')}</DialogTitle>
      <DialogContent>
        {confirmPayment && (() => {
          const { tableName, shortCode } = parseRef(confirmPayment.sourceReference || '');
          return (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {tableName}
              </Typography>
              {shortCode && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {shortCode}
                </Typography>
              )}
              <Typography variant="h5" fontWeight={700} color="error" sx={{ my: 2 }}>
                {formatCurrency(confirmPayment.totalAmount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pavoOrderTracking.cancelDialog.description')}
              </Typography>
            </Box>
          );
        })()}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button onClick={() => setConfirmPayment(null)} color="inherit">
          {t('pavoOrderTracking.cancelDialog.abort')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => confirmPayment && handleCancelPayment(confirmPayment)}
          disabled={!!cancellingId}
          startIcon={cancellingId ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('pavoOrderTracking.cancelDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default PavoOrderTrackingModal;
